import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSyncState } from '../sync/useSync';
import { appSandbox, checkForUpdate, installUpdate, useUpdates } from './updater';

const bar = 'flex items-center gap-3 border-b border-border bg-bg-subtle px-4 py-2 text-sm';

export function UpdateBanner() {
  const { t } = useTranslation();
  const { update, state, progress, dismissed } = useUpdates();
  if (!update || dismissed) return null;
  return (
    <div role="status" className={bar}>
      <span className="flex-1">
        {state === 'downloading'
          ? t('updates.downloading', { percent: Math.round((progress ?? 0) * 100) })
          : t('updates.available', { version: update.version })}
      </span>
      {state !== 'downloading' && (
        <>
          <button
            type="button"
            className="font-medium text-accent-ink"
            onClick={() => void installUpdate()}
          >
            {t('updates.install')}
          </button>
          <button
            type="button"
            className="underline"
            onClick={() => useUpdates.setState({ dismissed: true })}
          >
            {t('app.dismiss')}
          </button>
        </>
      )}
    </div>
  );
}

/** Settings → Updates (desktop only). */
export function UpdateSettings() {
  const { t } = useTranslation();
  const { update, state, error } = useUpdates();
  const signedIn = useSyncState((s) => !!s.session);
  const [version, setVersion] = useState<string | null>(null);
  const [flatpak, setFlatpak] = useState(false);

  useEffect(() => {
    void import('@tauri-apps/api/app').then(({ getVersion }) => getVersion().then(setVersion));
    void appSandbox().then((s) => setFlatpak(s === 'flatpak'));
  }, []);

  if (flatpak)
    return (
      <div className="flex flex-col gap-2 text-sm">
        <p>{t('updates.current', { version: version ?? '…' })}</p>
        <p className="text-xs text-fg-muted">{t('updates.flatpak')}</p>
      </div>
    );

  return (
    <div className="flex flex-col gap-2 text-sm">
      <p>{t('updates.current', { version: version ?? '…' })}</p>
      {!signedIn ? (
        <p className="text-xs text-fg-muted">{t('updates.signIn')}</p>
      ) : (
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={state === 'checking' || state === 'downloading'}
            onClick={() => void checkForUpdate()}
            className="rounded-lg border border-border px-2 py-1 hover:bg-bg-hover disabled:opacity-50"
          >
            {state === 'checking' ? t('updates.checking') : t('updates.check')}
          </button>
          {update && state !== 'downloading' && (
            <button
              type="button"
              onClick={() => void installUpdate()}
              className="rounded-full bg-accent px-2 py-1 font-medium text-accent-fg"
            >
              {t('updates.installVersion', { version: update.version })}
            </button>
          )}
          {state === 'none' && (
            <span className="text-xs text-fg-muted">{t('updates.upToDate')}</span>
          )}
        </div>
      )}
      {state === 'error' && error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
